import { Radio, type RadioOptionProps } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { RADIO_CHECKED_CLASSES, RADIO_DISABLED_CLASSES } from "./radioStateClasses";
import { SelectionDot } from "./SelectionDot";

const cardVariants = cva(
	"relative w-full flex items-center gap-4 rounded-md border border-bc-default bg-surface-base text-primary cursor-pointer select-none hover:border-bc-stronger hover:bg-surface-light focus-visible:ring focus-visible:ring-offset-0 focus-visible:ring-brand-accent focus-visible:ring-opacity-30",
	{
		variants: {
			size: {
				md: "max-w-[510px] px-6 py-5",
				sm: "max-w-[380px] px-4 py-3",
			},
		},
		defaultVariants: { size: "md" },
	},
);

type CardVariantProps = VariantProps<typeof cardVariants>;

export type RadioCardProps<TValue> = Omit<
	RadioOptionProps<"div", TValue>,
	"className" | "children"
> &
	CardVariantProps & {
		children?: ReactNode;
		className?: string;
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

const Root = <TValue,>({
	size,
	trackingId,
	className,
	children,
	ref,
	...rest
}: RadioCardProps<TValue>) => (
	<Radio
		ref={ref}
		{...rest}
		className={({ checked, disabled }) =>
			cn(
				cardVariants({ size }),
				checked && RADIO_CHECKED_CLASSES,
				disabled && RADIO_DISABLED_CLASSES,
				className,
			)
		}
		{...trackingAttrs(trackingId)}
	>
		{({ checked, disabled }) => (
			<>
				<SelectionDot checked={checked} size="md" state={disabled ? "disabled" : "default"} />
				<div className="flex flex-1 flex-col gap-2">{children}</div>
			</>
		)}
	</Radio>
);

type SlotProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

const Title: FC<SlotProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("text-[16px] font-semibold leading-[18px] text-primary", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Title.displayName = "RadioCard.Title";

const Description: FC<SlotProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("text-[14px] text-secondary", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Description.displayName = "RadioCard.Description";

type FooterProps = SlotProps & { leadingIcon?: ReactNode };

const Footer: FC<FooterProps> = ({
	leadingIcon,
	trackingId,
	className,
	children,
	ref,
	...rest
}) => (
	<div
		ref={ref}
		className={cn(
			"mt-[4px] flex items-center gap-[6px] text-[12px] font-medium leading-[15px] text-primary",
			className,
		)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{leadingIcon}
		<span>{children}</span>
	</div>
);
Footer.displayName = "RadioCard.Footer";

export const RadioCard = Object.assign(Root, { Title, Description, Footer });

export type RadioCardTitleProps = SlotProps;
export type RadioCardDescriptionProps = SlotProps;
export type RadioCardFooterProps = FooterProps;
